export function titleize(value: string): string {
  return value
    .split('_')
    .map((word) => `${word.charAt(0).toUpperCase()}${word.slice(1)}`)
    .join(' ')
}

export function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '—'
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'numeric',
    day: 'numeric',
    year: 'numeric',
  })
}

// Parses a YYYY-MM-DD date-only string as a local Date, then formats it with
// the given month style. Avoids UTC-midnight shifting the displayed day in
// negative-offset timezones (new Date("2026-02-04") parses as UTC midnight).
function formatNamedMonthDate(dateStr: string | null | undefined, month: 'short' | 'long'): string {
  if (!dateStr) return '—'
  const [yearStr, monthStr, dayStr] = dateStr.slice(0, 10).split('-')
  return new Date(Number(yearStr), Number(monthStr) - 1, Number(dayStr)).toLocaleDateString(
    'en-US',
    { month, day: 'numeric', year: 'numeric' }
  )
}

/** Formats a date string as "Feb 4, 2026". */
export function formatShortDate(dateStr: string | null | undefined): string {
  return formatNamedMonthDate(dateStr, 'short')
}

/** Formats a date string as "February 4, 2026". */
export function formatLongDate(dateStr: string | null | undefined): string {
  return formatNamedMonthDate(dateStr, 'long')
}

/**
 * Format an appointment date/time for display, showing day of week, date, and time.
 *
 * If an ISO datetime is provided (start_time), formats as "Wednesday, June 27, 11:00 AM EST".
 * If only a date string is provided, formats as "Wednesday, June 27, 2026" (parsed as local
 * date to avoid timezone shifting the day).
 *
 * @param date ISO 8601 date string (e.g., "2026-06-27")
 * @param startTime Optional ISO 8601 datetime string (e.g., "2026-06-27T15:00:00Z")
 */
export function formatAppointmentDateTime(date: string, startTime?: string | null): string {
  if (startTime) {
    return new Date(startTime).toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      timeZoneName: 'short',
    })
  }

  const [yearStr, monthStr, dayStr] = date.split('-')
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(Number(yearStr), Number(monthStr) - 1, Number(dayStr)))
}

export function formatRelativeDays(dateStr: string | null | undefined): number | string {
  if (!dateStr) return '—'
  const diffMs = Date.now() - new Date(dateStr).getTime()
  const days = Math.max(0, Math.floor(diffMs / (1000 * 60 * 60 * 24)))
  return days
}

export function formatRelativeTime(dateStr: string): string {
  const diffMs = Date.now() - new Date(dateStr).getTime()
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
  const diffMins = Math.floor(diffMs / (1000 * 60))

  if (diffDays >= 1) return `${diffDays}d`
  if (diffHours >= 1) return `${diffHours}h`
  if (diffMins < 1) return '<1m'
  return `${diffMins}m`
}

export function formatMessageTimestamp(value: string | null | undefined): string {
  if (!value) return '—'
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return '—'
  return date.toLocaleString('en-US', {
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })
}

export function formatPhoneNumber(phoneNumber: string | null | undefined): string {
  if (!phoneNumber) return '—'

  const digits = phoneNumber.replace(/\D/g, '')
  const hasUsCountryCode = digits.length > 10 && digits.startsWith('1')
  const nationalNumber = hasUsCountryCode ? digits.slice(1, 11) : digits.slice(0, 10)
  const extension = hasUsCountryCode ? digits.slice(11) : digits.slice(10)

  if (nationalNumber.length !== 10) return phoneNumber

  const formatted = nationalNumber.replace(/(\d{3})(\d{3})(\d{4})/, '($1) $2-$3')
  return extension ? `${formatted} ext. ${extension}` : formatted
}
